import ReactGA from 'react-ga4';
import classes from "./Card.module.css";
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import regionAnalyzer from "./CardUtils";
import CurrencyContext from "../../store/CurrencyContext";

const Card = props => {
    const [urlModified, setUrlModified] = useState(false);
    const [adjustedURL, setAdjustedURL] = useState('');
    const [region, setRegion] = useState('');
    const { t } = useTranslation('common');
    const currencyCtx = useContext(CurrencyContext);
    // const languageParentWindow = currencyCtx.languageParentWindow;
    const { url, referal, backupUrl, validity, price, provider, country, location, planName } = props;
    const isVideotron = provider?.toLowerCase() === "videotron";

    const parsedValidity = parseInt(validity);

    const adjustCountryUrl = (country) => country.toLowerCase().replaceAll(' ', '-');

    const brandNameClass = provider && provider.length > 10 
        ? `${classes.brandName} ${classes.longBrandName}` 
        : classes.brandName;

    const handleLinkClick = () => {
        ReactGA.event({
            category: 'link_click',
            action: 'click_on_offer_link',
            label: planName || 'None'
        });

        ReactGA.event({
            category: 'referal_link_click',
            action: 'collect_referal_clicks',
            label: referal || 'None'
        });
    };

    const getAdjustedURL = () => {
        let adjustedURL = url;
        const countryURLRedirection = adjustCountryUrl(country);
        switch (provider) {
            case 'Airalo':
                // const urlAdjustedForLanguage = languageParentWindow === 'en' 
                //     ? "&u=https%3A%2F%2Fwww.airalo.com%2F"
                //     : "&u=https%3A%2F%2Fwww.airalo.com%2Ffr%2F";
                // adjustedURL += urlAdjustedForLanguage + countryURLRedirection + "-esim";
                break;
            case 'Nomad':
                // adjustedURL = adjustedURL.replace("getnomad.app/", `getnomad.app/${countryURLRedirection}-eSIM`);
                break;
            case 'Flexiroam':
            case 'Esimatic':
            case 'Saily':
            case 'AlwaysMobile':
                adjustedURL = adjustedURL.replace("united-states", countryURLRedirection);
                break;
            case 'RedTeaGo':
                const subidIndex = adjustedURL.indexOf("&subid");
                adjustedURL = adjustedURL.slice(0, subidIndex) + "esim-" + countryURLRedirection + "?c=z04v9ddd" + adjustedURL.slice(subidIndex);
                break;
            default:
                console.warn('No provider');
        }

        return adjustedURL;
    };

    useEffect(() => {
        setRegion(location);
        const newAdjustedURL = getAdjustedURL();
        setAdjustedURL(newAdjustedURL);
        setUrlModified(true);

        const variable = regionAnalyzer(provider, planName);
        if (variable) setRegion(t(variable));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, planName, location]);

    const renderCapacityIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cell-signal-5" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M20 20h-15.269a.731 .731 0 0 1 -.517 -1.249l14.537 -14.537a.731 .731 0 0 1 1.249 .517v15.269z"></path>
            <path d="M16 7v13"></path>
            <path d="M12 20v-9"></path>
            <path d="M8 20v-5"></path>
        </svg>
    );

    const renderLocationIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-map-pin" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
            <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"></path>
        </svg>
    );

    const renderValidityIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M12.5 21h-6.5a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v5"></path>
            <path d="M16 3v4"></path>
            <path d="M8 3v4"></path>
            <path d="M4 11h16"></path>
            <path d="M16 19h6"></path>
            <path d="M19 16v6"></path>
        </svg>
    );

    const formatPrice = (price, currency, locale) => {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price);
    };
    
    const capacityText = props.capacity >= 1 
        ? `${props.capacity} ${t('capacity.giga')}` 
        : `${props.capacity * 1000} ${t('capacity.mega')}`;

    const validityText = parsedValidity === 0
        ? `${t('offer.unlimitedValidity')}`
        : `${t('offer.validity')} ${parsedValidity} ${t('offer.days')}`;

    return (
        
        <div className={`${classes.card} ${isVideotron ? classes.videotronHighlight : ''}`}>
            {isVideotron && (
                <div className={classes.badge}>{t('offer.recommended')}</div>
            )}
            <div className={classes.flexContainer}>
                <div className={`${classes.flexContainer} ${classes.firstColumn}`}>
                    <img className="" src={props.logo} alt='logo'/>
                    <span className={brandNameClass}>{provider}</span>
                </div>
                <div className={classes.secondColumn}>
                    <span className={classes.offerTitle}><b>{planName}</b></span>
                    <div className={classes.description}>
                        <div className={classes.inlineContainer}>
                            <span className={classes.materialSymbolsOutlined}>
                                {renderCapacityIcon()}
                            </span>
                            {capacityText}
                        </div>
                        <div className={classes.inlineContainer}>
                            <span className="ph-icon-location">
                                {renderLocationIcon()}
                            </span> {region}
                        </div>
                        <div className={classes.inlineContainer}>
                            {renderValidityIcon()}
                            <span>{validityText}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${classes.flexContainer} ${classes.scdLineContainer}`}>
                <div key="price" className={`${classes.firstColumn} ${classes.planSizeContainer}`}>
                    {formatPrice(price, currencyCtx.selectedCurrency, currencyCtx.locale)}
                </div>
                <a 
                    key="link"
                    className={classes.secondColumn} 
                    href={urlModified ? adjustedURL : backupUrl} 
                    target="_blank" 
                    rel="noreferrer"
                    onClick={handleLinkClick}
                >
                    {t('offer.check')}
                </a>
            </div>
        </div>
    );
};

export default Card;